import { guidFor } from '@ember/object/internals';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  applyPageHeaderImageCrop,
  applyPageHeaderImageHeight,
  canCropPageHeaderImage,
  createPageHeaderImage,
  DEFAULT_HEADER_IMAGE_HEIGHT,
  getPageHeaderImageLegacyTop,
  getPageHeaderImageViewportHeight,
  replacePageHeaderImageSource,
  TED_PAGE_CONTENT_WIDTH,
} from 'admin-panel/utils/page-header-image';
import {
  HEADER_IMAGE_CENTER_Y,
  headerImageCropYFromLegacyPosition,
} from 'admin-panel/utils/page-header-image-crop';

import type ImageModel from 'admin-panel/models/image';
import type { PageHeaderImageBlock } from 'admin-panel/utils/page-header-image';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type IntlService from 'ember-intl/services/intl';

interface PanelCategoryPageHeaderImageSignature {
  Args: {
    contextKey: string;
    value?: PageHeaderImageBlock | null;
    canShare?: boolean;
    isShared?: boolean;
    hasPendingRemoval?: boolean;
    isSharingLoading?: boolean;
    sharingLoadFailed?: boolean;
    onEnsureSharing?: () => Promise<boolean>;
    onApplyOptions?: (
      value: PageHeaderImageBlock | null,
      isShared: boolean
    ) => void;
    getReplacementCount?: (value: PageHeaderImageBlock | null) => number;
    onChange: (value: PageHeaderImageBlock | null) => void;
    onCroppingChange: (isCropping: boolean) => void;
  };
}

export default class PanelCategoryPageHeaderImageComponent extends Component<PanelCategoryPageHeaderImageSignature> {
  @service
  declare intl: IntlService;

  @service
  declare flashMessages: FlashMessageService;

  readonly overlayActionClass =
    'pointer-events-none shrink-0 gap-1.5 opacity-0 shadow-lg transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(any-pointer:coarse)]:pointer-events-auto [@media(any-pointer:coarse)]:opacity-100';

  private triggerButtonElement: HTMLButtonElement | null = null;
  private optionsButtonElement: HTMLButtonElement | null = null;
  private repositionButtonElement: HTMLButtonElement | null = null;
  private previewImageElement: HTMLImageElement | null = null;
  readonly repositionDescriptionId = `${guidFor(this)}-reposition-description`;

  @tracked
  isPickerOpen = false;

  @tracked
  isOptionsOpen = false;

  @tracked
  replacementCount = 0;

  @tracked
  isReplacementConfirmationOpen = false;

  @tracked
  pendingOptions: {
    header: PageHeaderImageBlock | null;
    isShared: boolean;
  } | null = null;

  private isOptionsSaveClosing = false;
  private optionsHeaderHtml: string | null = null;
  private handledValueHtml = this.args.value?.html ?? null;

  @tracked
  isCropping = false;

  @tracked
  draftCropY = HEADER_IMAGE_CENTER_Y;

  @tracked
  readyPreview: {
    src: string;
    width: number;
    height: number;
    aspectRatio: number;
  } | null = null;

  get persistedViewportHeight() {
    return this.args.value
      ? getPageHeaderImageViewportHeight(this.args.value)
      : null;
  }

  get viewportHeight() {
    return this.persistedViewportHeight ?? DEFAULT_HEADER_IMAGE_HEIGHT;
  }

  get editableViewportHeight() {
    const value = this.args.value;

    return value && canCropPageHeaderImage(value)
      ? getPageHeaderImageViewportHeight(value)
      : null;
  }

  get optionsModalHeight() {
    return this.pendingOptions?.header
      ? getPageHeaderImageViewportHeight(this.pendingOptions.header)
      : this.editableViewportHeight;
  }

  get isRemovalReplacementConfirmation() {
    return this.pendingOptions?.header === null;
  }

  get optionsModalIsShared() {
    return this.pendingOptions?.isShared ?? Boolean(this.args.isShared);
  }

  get hasPendingOptions() {
    return Boolean(this.pendingOptions);
  }

  get optionsImageUrl() {
    return this.args.value?.src ?? '';
  }

  get previewAspectStyle() {
    return htmlSafe(
      `aspect-ratio: ${TED_PAGE_CONTENT_WIDTH} / ${this.viewportHeight};`
    );
  }

  get previewImageClass() {
    if (this.usesCroppedPreview) {
      return 'h-full w-full object-cover';
    }

    return this.persistedViewportHeight === null
      ? 'h-full w-full object-cover object-center'
      : 'absolute left-0 top-0 block h-auto w-full max-w-full';
  }

  get canReposition() {
    const value = this.args.value;
    const preview = this.readyPreview;

    return Boolean(
      value &&
        preview?.src === value.src &&
        canCropPageHeaderImage(value) &&
        preview.aspectRatio < TED_PAGE_CONTENT_WIDTH / this.viewportHeight
    );
  }

  get previewImageWidth() {
    return this.readyPreview?.width ?? 0;
  }

  get previewImageHeight() {
    return this.readyPreview?.height ?? 0;
  }

  get legacyPreviewTop() {
    const value = this.args.value;

    return value ? getPageHeaderImageLegacyTop(value) : null;
  }

  get usesCroppedPreview() {
    return this.isCropping || this.args.value?.cropY !== null;
  }

  get previewStyle() {
    if (this.isCropping) {
      return htmlSafe(`object-position: 50% ${this.draftCropY}%;`);
    }

    const legacyTop =
      this.persistedViewportHeight === null ? null : this.legacyPreviewTop;

    if (legacyTop !== null) {
      return htmlSafe(`top: ${legacyTop}%; transform: translateY(-50%);`);
    }

    if (
      this.args.value?.cropY !== null &&
      this.args.value?.cropY !== undefined
    ) {
      return htmlSafe(`object-position: 50% ${this.args.value.cropY}%;`);
    }

    return undefined;
  }

  get repositionUnavailableReason() {
    const value = this.args.value;
    const preview = this.readyPreview;

    if (!value || preview?.src !== value.src) {
      return this.intl.t(
        'panels.category-list.page-editor.header-image.reposition-unavailable-loading'
      );
    }

    if (!canCropPageHeaderImage(value)) {
      return this.intl.t(
        'panels.category-list.page-editor.header-image.reposition-unavailable-format'
      );
    }

    return this.intl.t(
      'panels.category-list.page-editor.header-image.reposition-unavailable-overflow'
    );
  }

  @action
  async openPicker() {
    if (!(await this.ensureSharing())) return;

    this.isPickerOpen = true;
  }

  @action
  closePicker() {
    this.isPickerOpen = false;
  }

  @action
  async openOptions() {
    if (!(await this.ensureSharing())) return;

    this.optionsHeaderHtml = this.args.value?.html ?? null;
    this.isOptionsOpen = true;
  }

  @action
  closeOptions() {
    this.isOptionsOpen = false;
    this.isOptionsSaveClosing = false;
    this.optionsHeaderHtml = null;
    if (!this.isReplacementConfirmationOpen) this.pendingOptions = null;
  }

  @action
  closeReplacementConfirmation() {
    this.isReplacementConfirmationOpen = false;
    this.isOptionsOpen = true;
  }

  @action
  confirmReplacement() {
    const pending = this.pendingOptions;
    if (!pending) return;

    this.applyOptions(pending.header, pending.isShared);
    this.pendingOptions = null;
    this.replacementCount = 0;
    this.isReplacementConfirmationOpen = false;
    this.restoreOptionsFocus();
  }

  @action
  handleContextChange() {
    this.closePicker();
    this.closeOptions();
    this.pendingOptions = null;
    this.isReplacementConfirmationOpen = false;
    this.discardCrop(false);
  }

  @action
  handleValueChange() {
    const valueHtml = this.args.value?.html ?? null;
    if (valueHtml === this.handledValueHtml) return;

    this.handledValueHtml = valueHtml;
    if (
      !this.isOptionsSaveClosing &&
      this.args.value?.html !== this.optionsHeaderHtml
    ) {
      this.closeOptions();
    }
    this.discardCrop(false);
    if (this.args.value?.src !== this.readyPreview?.src) {
      this.readyPreview = null;
    }
  }

  @action
  registerTriggerButton(element: Element) {
    if (element instanceof HTMLButtonElement) {
      this.triggerButtonElement = element;
    }
  }

  @action
  registerOptionsButton(element: Element) {
    if (element instanceof HTMLButtonElement) {
      this.optionsButtonElement = element;
    }
  }

  @action
  clearOptionsButton(element: Element) {
    if (this.optionsButtonElement === element) {
      this.optionsButtonElement = null;
    }
  }

  @action
  clearTriggerButton(element: Element) {
    if (this.triggerButtonElement === element) {
      this.triggerButtonElement = null;
    }
  }

  @action
  registerRepositionButton(element: Element) {
    if (element instanceof HTMLButtonElement) {
      this.repositionButtonElement = element;
    }
  }

  @action
  clearRepositionButton(element: Element) {
    if (this.repositionButtonElement === element) {
      this.repositionButtonElement = null;
    }
  }

  @action
  registerPreviewImage(element: Element) {
    if (element instanceof HTMLImageElement) {
      this.previewImageElement = element;
      this.syncReadyPreview();
    }
  }

  @action
  clearPreviewImage(element: Element) {
    if (this.previewImageElement === element) {
      this.previewImageElement = null;
      this.readyPreview = null;
    }
  }

  @action
  handlePreviewLoad(event: Event) {
    if (event.currentTarget === this.previewImageElement) {
      this.syncReadyPreview();
    }
  }

  @action
  handlePreviewError(event: Event) {
    const image = event.currentTarget;

    if (
      image instanceof HTMLImageElement &&
      image === this.previewImageElement
    ) {
      this.readyPreview = null;
    }
  }

  @action
  async startCropping() {
    if (!(await this.ensureSharing())) return;
    if (!this.canReposition) return;

    this.closePicker();
    this.draftCropY =
      this.args.value?.cropY ??
      this.currentPreviewCropY ??
      HEADER_IMAGE_CENTER_Y;
    this.isCropping = true;
    this.args.onCroppingChange(true);
  }

  @action
  cancelCropping() {
    this.discardCrop(true);
  }

  @action
  saveCrop() {
    const value = this.args.value;

    if (!value) return;

    const cropped = applyPageHeaderImageCrop(value, this.draftCropY);
    if (!cropped) return;

    if (cropped.html !== value.html) this.args.onChange(cropped);
    this.discardCrop(true);
  }

  @action
  saveOptions(height: number | null, isShared: boolean) {
    const value = this.args.value;
    if (!value && !this.args.hasPendingRemoval) return false;

    const resized: PageHeaderImageBlock | null = value
      ? height === null
        ? value
        : applyPageHeaderImageHeight(value, height)
      : null;
    if (value && !resized) {
      console.error('Failed to apply the header image height', { height });
      this.flashMessages.danger(
        this.intl.t(
          'panels.category-list.page-editor.header-image.options.height-failed'
        )
      );

      return false;
    }

    if (isShared && !this.args.isShared) {
      const replacementCount = this.args.getReplacementCount?.(resized) ?? 0;

      if (replacementCount > 0) {
        this.pendingOptions = { header: resized, isShared };
        this.replacementCount = replacementCount;
        this.isOptionsOpen = false;
        this.isReplacementConfirmationOpen = true;

        return true;
      }
    }

    this.isOptionsSaveClosing = true;
    this.applyOptions(resized, isShared);

    return true;
  }

  @action
  async selectImage(image: ImageModel) {
    if (!(await this.ensureSharing())) return;

    const source = image.contentLargeUrl;
    const headerImage = this.args.value
      ? replacePageHeaderImageSource(this.args.value, source)
      : createPageHeaderImage(source);

    if (!headerImage) return;

    const replacesTrigger = !this.args.value;
    this.closePicker();
    this.args.onChange(headerImage);
    if (replacesTrigger) this.restoreFocus();
  }

  @action
  async removeImage() {
    if (!(await this.ensureSharing())) return;

    this.args.onChange(null);
    this.restoreFocus();
  }

  private restoreFocus() {
    next(() => {
      if (this.isDestroying || this.isDestroyed) return;

      this.triggerButtonElement?.focus();
    });
  }

  private restoreOptionsFocus() {
    next(() => {
      if (this.isDestroying || this.isDestroyed) return;

      this.optionsButtonElement?.focus();
    });
  }

  private discardCrop(restoreFocus: boolean) {
    if (!this.isCropping) return;

    this.isCropping = false;
    this.draftCropY = HEADER_IMAGE_CENTER_Y;

    this.args.onCroppingChange(false);

    if (restoreFocus) {
      next(() => {
        if (this.isDestroying || this.isDestroyed) return;

        this.repositionButtonElement?.focus();
      });
    }
  }

  @action
  updateDraftCropY(cropY: number) {
    if (cropY !== this.draftCropY) this.draftCropY = cropY;
  }

  @action
  async retrySharingLoad() {
    if ((await this.ensureSharing()) || this.args.sharingLoadFailed) {
      this.restoreFocus();
    }
  }

  private ensureSharing() {
    return this.args.onEnsureSharing?.() ?? Promise.resolve(true);
  }

  private applyOptions(value: PageHeaderImageBlock | null, isShared: boolean) {
    if (this.args.onApplyOptions) {
      this.args.onApplyOptions(value, isShared);
      return;
    }

    this.args.onChange(value);
  }

  private syncReadyPreview() {
    const image = this.previewImageElement;
    const source = image?.getAttribute('src') ?? null;

    this.readyPreview =
      image?.complete &&
      image.naturalWidth > 0 &&
      image.naturalHeight > 0 &&
      source
        ? {
            src: source,
            width: image.naturalWidth,
            height: image.naturalHeight,
            aspectRatio: image.naturalWidth / image.naturalHeight,
          }
        : null;
  }

  private get currentPreviewCropY() {
    const image = this.previewImageElement;
    const viewport = image?.parentElement;
    if (!image || !viewport) return null;

    const imageRect = image.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    return headerImageCropYFromLegacyPosition(
      imageRect.top,
      imageRect.height,
      viewportRect.top,
      viewportRect.height
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Panel::Category::PageHeaderImage': typeof PanelCategoryPageHeaderImageComponent;
    'panel/category/page-header-image': typeof PanelCategoryPageHeaderImageComponent;
  }
}
